import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for managing authentication state and navigation
 * Eliminates duplicate token/username checking logic across components
 * Now handles intended destination redirects after authentication
 */
const useAuth = () => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
    username: null,
    token: null,
    error: null,
  });

  // Check authentication status on mount and when storage changes
  useEffect(() => {
    const checkAuth = () => {
      try {
        const token = localStorage.getItem('gameToken');
        const username = localStorage.getItem('username');

        // User is authenticated if they have both token and username
        // This works for both OAuth users (JWT) and guest users (guest_ tokens)
        const isAuthenticated = !!(token && username);

        setAuthState({
          isAuthenticated,
          isLoading: false,
          username: username || null,
          token: token || null,
          error: null,
        });

        if (isAuthenticated) {
          console.log('🔐 Auth check: User authenticated as', username);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to check authentication',
        }));
      }
    };

    checkAuth();

    // Listen for storage changes (useful for multi-tab sync)
    const handleStorageChange = (e) => {
      if (e.key === 'gameToken' || e.key === 'username') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Set intended destination before redirecting to login
  const setIntendedDestination = useCallback((path) => {
    localStorage.setItem('intendedDestination', path);
  }, []);

  // Get and clear intended destination
  const getAndClearIntendedDestination = useCallback(() => {
    const destination = localStorage.getItem('intendedDestination');
    localStorage.removeItem('intendedDestination');
    return destination;
  }, []);

  // Require authentication - redirects to home if not authenticated
  // Now saves the current path as intended destination
  // TODO: Token claiming page is temporarily hidden - using Ko-fi donations instead
  // This should auto-generate guest username/token in the future
  const requireAuth = useCallback(
    (currentPath = null) => {
      if (!authState.isLoading && !authState.isAuthenticated) {
        // Save where the user was trying to go
        if (currentPath) {
          setIntendedDestination(currentPath);
        }

        console.log(
          'Authentication required, redirecting to home. Intended destination:',
          currentPath
        );
        navigate('/');
        return false;
      }
      return authState.isAuthenticated;
    },
    [authState.isAuthenticated, authState.isLoading, navigate, setIntendedDestination]
  );

  // Login function - saves token and username, then redirects to intended destination
  const login = useCallback((username, token) => {
    try {
      localStorage.setItem('gameToken', token);
      localStorage.setItem('username', username);
      localStorage.setItem('tokenCreated', new Date().toISOString());

      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        username,
        token,
        error: null,
      });

      return true;
    } catch (error) {
      console.error('Error during login:', error);
      setAuthState((prev) => ({
        ...prev,
        error: 'Failed to save authentication data',
      }));
      return false;
    }
  }, []);

  // Logout function - clears all auth data
  const logout = useCallback(() => {
    try {
      localStorage.removeItem('gameToken');
      localStorage.removeItem('username');
      localStorage.removeItem('tokenCreated');
      localStorage.removeItem('currentRoom');
      localStorage.removeItem('isHost');
      localStorage.removeItem('gameState');
      localStorage.removeItem('intendedDestination'); // Clear intended destination on logout

      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        username: null,
        token: null,
        error: null,
      });

      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      setAuthState((prev) => ({
        ...prev,
        error: 'Failed to clear authentication data',
      }));
    }
  }, [navigate]);

  // Update username (for changing username feature)
  const updateUsername = useCallback(
    (newUsername) => {
      if (!authState.token) {
        console.error('Cannot update username without token');
        return false;
      }

      try {
        localStorage.setItem('username', newUsername);
        setAuthState((prev) => ({
          ...prev,
          username: newUsername,
        }));
        return true;
      } catch (error) {
        console.error('Error updating username:', error);
        return false;
      }
    },
    [authState.token]
  );

  // Get room-related data
  const getRoomData = useCallback(() => {
    return {
      roomCode: localStorage.getItem('currentRoom'),
      isHost: localStorage.getItem('isHost') === 'true',
      gameState: localStorage.getItem('gameState'),
      isSpectator: localStorage.getItem('isSpectator') === 'true',
    };
  }, []);

  // Clear room data (useful when leaving a room)
  const clearRoomData = useCallback(() => {
    localStorage.removeItem('currentRoom');
    localStorage.removeItem('isHost');
    localStorage.removeItem('gameState');
    localStorage.removeItem('isSpectator');
  }, []);

  return {
    // State
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    username: authState.username,
    token: authState.token,
    error: authState.error,

    // Methods
    requireAuth,
    login,
    logout,
    updateUsername,
    getRoomData,
    clearRoomData,
    setIntendedDestination,
    getAndClearIntendedDestination,
  };
};

export default useAuth;
